import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authApi } from "../../api/auth";
import { errorMessage } from "../../api/client";
import { AuthLayout } from "../../components/AuthLayout";
import { Button, CodeInput, ErrorText, Field, TextInput } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";

const COOLDOWN = 60;

export default function EmployeeLogin() {
  const { session, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && session?.authenticated && session.role === "EMPLOYEE") navigate("/me", { replace: true });
  }, [loading, session, navigate]);

  return (
    <AuthLayout
      testId="employee-login-page"
      backdrop="crew"
      eyebrow="Crew"
      title={step === "email" ? "Welcome Crew" : "Check your email"}
      subtitle={step === "email" ? "We'll email you a one-time 6-digit code." : (notice ?? "")}
    >
      {step === "email" ? (
        <EmailStep email={email} setEmail={setEmail} onSent={(msg) => { setNotice(msg); setStep("code"); }} />
      ) : (
        <CodeStep
          email={email}
          onBack={() => setStep("email")}
          onDone={async () => {
            await refresh();
            navigate("/me", { replace: true });
          }}
        />
      )}
    </AuthLayout>
  );
}

function EmailStep({ email, setEmail, onSent }: { email: string; setEmail: (v: string) => void; onSent: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.requestEmployeeCode(email);
      onSent(res.message);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="employee-email-form">
      <Field label="Work email" htmlFor="employee-email">
        <TextInput id="employee-email" type="email" autoComplete="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} data-testid="employee-login-email-input" />
      </Field>
      <ErrorText message={error} testId="employee-request-code-error" />
      <Button type="submit" loading={busy} className="w-full" data-testid="employee-request-code-button">
        Email me a code
      </Button>
    </form>
  );
}

function CodeStep({ email, onBack, onDone }: { email: string; onBack: () => void; onDone: () => Promise<void> }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(COOLDOWN);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await authApi.verifyEmployeeCode(email, value);
      await onDone();
    } catch (err) {
      setError(errorMessage(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const res = await authApi.requestEmployeeCode(email);
      toast.success(res.message);
      setCooldown(COOLDOWN);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-6" data-testid="employee-code-form">
      <p className="text-sm text-zinc-400">
        Code sent to <span data-testid="employee-code-sent-to" className="text-white">{email}</span>. It expires in 10 minutes.
      </p>
      <CodeInput value={code} onChange={setCode} onComplete={(v) => void submit(v)} testId="employee-code-input" autoFocus />
      <ErrorText message={error} testId="employee-verify-code-error" />
      <Button type="submit" loading={busy} disabled={code.length !== 6} className="w-full" data-testid="employee-verify-code-button">
        Sign in
      </Button>
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" onClick={onBack} className="px-0" data-testid="employee-change-email-button">
          Change email
        </Button>
        <Button type="button" variant="ghost" onClick={resend} disabled={cooldown > 0} className="px-0" data-testid="employee-resend-code-button">
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </Button>
      </div>
    </form>
  );
}
